/**
 * Map layer component that renders segment location point clusters.
 */
import { useEffect, useCallback } from 'react';
import { useMap } from '../../components/ui/map';
import type { Segment } from '../../types/map';

interface SegmentPointLayerProps {
  segmentsGeoJson: GeoJSON.FeatureCollection;
  segments: Segment[];
  onSegmentClick: (segment: Segment) => void;
}

export function SegmentPointLayer({ segmentsGeoJson, segments, onSegmentClick }: SegmentPointLayerProps) {
  const { map, isLoaded } = useMap();

  const handleSegmentSelect = useCallback(
    (id: string) => {
      const seg = segments.find((s) => s.id === id);
      if (seg) onSegmentClick(seg);
    },
    [segments, onSegmentClick],
  );

  useEffect(() => {
    if (!map || !isLoaded) return;

    const sourceId = 'segments-source';
    const clusterLayerId = 'segments-clusters';
    const clusterCountLayerId = 'segments-cluster-count';
    const unclusteredLayerId = 'segments-unclustered';

    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: 'geojson',
        data: segmentsGeoJson as any,
        cluster: true,
        clusterRadius: 50,
        clusterMaxZoom: 14,
      });
    }

    if (!map.getLayer(clusterLayerId)) {
      map.addLayer({
        id: clusterLayerId,
        type: 'circle',
        source: sourceId,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': ['step', ['get', 'point_count'], '#93c5fd', 50, '#60a5fa', 200, '#3b82f6'],
          'circle-radius': ['step', ['get', 'point_count'], 16, 50, 20, 200, 24],
          'circle-opacity': 0.85,
        },
      });
    }

    if (!map.getLayer(clusterCountLayerId)) {
      map.addLayer({
        id: clusterCountLayerId,
        type: 'symbol',
        source: sourceId,
        filter: ['has', 'point_count'],
        layout: { 'text-field': '{point_count_abbreviated}', 'text-size': 12 },
        paint: { 'text-color': '#0f172a' },
      });
    }

    if (!map.getLayer(unclusteredLayerId)) {
      map.addLayer({
        id: unclusteredLayerId,
        type: 'circle',
        source: sourceId,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': ['case', ['>', ['get', 'postCount'], 0], '#3b82f6', '#94a3b8'],
          'circle-radius': 6,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
    }

    const handleClusterClick = (e: any) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [clusterLayerId] });
      const clusterId = features?.[0]?.properties?.cluster_id;
      if (clusterId == null) return;
      const source = map.getSource(sourceId) as any;
      source.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
        if (err) return;
        map.easeTo({ center: (features[0].geometry as any).coordinates, zoom });
      });
    };

    const handleUnclusteredClick = (e: any) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [unclusteredLayerId] });
      const id = features?.[0]?.properties?.id as string | undefined;
      if (id) handleSegmentSelect(id);
    };

    map.on('click', clusterLayerId, handleClusterClick);
    map.on('click', unclusteredLayerId, handleUnclusteredClick);
    map.on('mouseenter', clusterLayerId, () => (map.getCanvas().style.cursor = 'pointer'));
    map.on('mouseleave', clusterLayerId, () => (map.getCanvas().style.cursor = ''));
    map.on('mouseenter', unclusteredLayerId, () => (map.getCanvas().style.cursor = 'pointer'));
    map.on('mouseleave', unclusteredLayerId, () => (map.getCanvas().style.cursor = ''));

    return () => {
      map.off('click', clusterLayerId, handleClusterClick);
      map.off('click', unclusteredLayerId, handleUnclusteredClick);
      if (map.getLayer(unclusteredLayerId)) map.removeLayer(unclusteredLayerId);
      if (map.getLayer(clusterCountLayerId)) map.removeLayer(clusterCountLayerId);
      if (map.getLayer(clusterLayerId)) map.removeLayer(clusterLayerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    };
  }, [handleSegmentSelect, isLoaded, map, segmentsGeoJson]);

  // Keep source data in sync
  useEffect(() => {
    if (!map || !isLoaded) return;
    const src = map.getSource('segments-source') as any;
    src?.setData?.(segmentsGeoJson as any);
  }, [isLoaded, map, segmentsGeoJson]);

  return null;
}
